import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatCurrency } from '@/services/order-service';
import { OrderItemResponse, OrderResponse, OrderStatus } from '@/types/order-type';
import { Edit2, Package } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useEditOrderItem } from '../sale/hooks/use-edit-order-item';
import { OrderItemEditDialog } from './OrderItemEditDialog';
import { ProductDetailImag } from './ProductDetailImag';

interface PublicOrderItemsProps {
  order: OrderResponse;
  orderItems: OrderItemResponse[];
  isLogin: boolean;
  orderStatus: OrderStatus;
  onRefresh?: () => void;
}

export function PublicOrderItems({
  order,
  orderItems,
  isLogin,
  orderStatus,
  onRefresh,
}: PublicOrderItemsProps) {
  const subtotal = useMemo(
    () => orderItems.reduce((sum, it) => sum + Number(it.price) * it.quantity, 0),
    [orderItems],
  );

  const totalQty = useMemo(
    () => orderItems.reduce((sum, it) => sum + it.quantity, 0),
    [orderItems],
  );

  const canEdit = useMemo(() => {
    const allowedStatuses = ['DRAFTED', 'CONFIRMED'];
    return isLogin && allowedStatuses.includes(orderStatus);
  }, [isLogin, orderStatus]);
  const { handleUpdateItem, setEditingItem, editingItem } = useEditOrderItem(order, () => {
    onRefresh?.();
  });
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  return (
    <Card className="border border-slate-200 shadow-sm rounded-xl overflow-hidden">
      <CardHeader className="py-3 px-4 border-b bg-slate-50/50">
        <h3 className="text-sm font-semibold text-slate-800">Order Items</h3>
      </CardHeader>

      <CardContent className="p-0">
        {orderItems.length > 0 ? (
          <div className="max-h-[500px] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/70 sticky top-0 z-10">
                  <TableHead className="py-2.5 px-4 text-xs font-semibold">Item</TableHead>
                  <TableHead className="py-2.5 px-4 text-xs font-semibold text-right">
                    Price
                  </TableHead>
                  <TableHead className="py-2.5 px-4 text-xs font-semibold text-center">
                    Qty
                  </TableHead>
                  <TableHead className="py-2.5 px-4 text-xs font-semibold text-right">
                    Total
                  </TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orderItems.map((item, idx) => {
                  const total = Number(item.price) * item.quantity;

                  return (
                    <TableRow
                      key={item.id}
                      className={`group cursor-pointer hover:bg-blue-50/30 transition-colors ${
                        idx % 2 ? 'bg-white' : 'bg-slate-50/30'
                      }`}
                      onDoubleClick={() => canEdit && setEditingItem(item)} // Double click for desktop
                    >
                      <TableCell className="py-3 px-4">
                        <div className="flex items-start gap-3">
                          <div className="min-w-0">
                            <div
                              className="cursor-pointer"
                              onClick={() => item.imageUrl && setPreviewImage(item.imageUrl)}
                            >
                              <ProductDetailImag item={item} />
                            </div>
                            <Badge className="mt-1 bg-blue-500 hover:bg-blue-600 text-white text-xs font-medium">
                              {item.sku || 'Unknown Product'}
                            </Badge>
                          </div>
                        </div>
                      </TableCell>

                      <TableCell className="py-3 px-4 text-right align-middle">
                        <span className="text-sm font-mono text-slate-800">
                          {formatCurrency(item.price)}
                        </span>
                      </TableCell>

                      <TableCell className="py-3 px-4 text-center align-middle">
                        <span className="inline-flex h-7 px-2 items-center justify-center rounded-md border border-slate-200 bg-white text-xs font-bold text-slate-700 shadow-sm">
                          {item.quantity}
                        </span>
                      </TableCell>

                      <TableCell className="py-3 px-4 text-right align-middle">
                        <span className="text-sm font-semibold text-slate-900 font-mono">
                          {formatCurrency(String(total))}
                        </span>
                      </TableCell>

                      <TableCell className="py-3 px-2 text-center">
                        {canEdit && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-slate-400 hover:text-blue-600 hover:bg-blue-50"
                            onClick={() => setEditingItem(item)}
                          >
                            <Edit2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="p-6">
            <Alert className="border-amber-200 bg-amber-50">
              <Package className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-sm text-amber-800">
                No items found in this order.
              </AlertDescription>
            </Alert>
          </div>
        )}
      </CardContent>

      {/* Footer Summary */}
      <div className="border-t bg-slate-50/50 p-4 space-y-1">
        <div className="flex justify-between text-xs text-slate-500">
          <span>Total Quantity:</span>
          <span className="font-mono font-medium text-slate-700">{totalQty}</span>
        </div>
        <div className="flex justify-between text-sm font-bold">
          <span className="text-slate-700">Subtotal:</span>
          <span className="font-mono text-blue-700">{formatCurrency(String(subtotal))}</span>
        </div>
      </div>
      <OrderItemEditDialog
        item={editingItem}
        isOpen={!!editingItem}
        onClose={() => setEditingItem(null)}
        onSave={handleUpdateItem}
      />
      <Dialog open={!!previewImage} onOpenChange={(open) => !open && setPreviewImage(null)}>
        <DialogContent className="max-w-4xl w-[95vw] p-0 sm:rounded-lg overflow-hidden">
          <div className="relative flex items-center justify-center min-h-[50vh] max-h-[90vh] p-4">
            {previewImage && (
              <img
                src={previewImage}
                alt="Product preview"
                className="max-w-full max-h-[85vh] object-contain"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
